import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface VideoCardProps {
  id: number;
  title: string;
  thumbnail: string;
  duration: string;
  views: number;
  rating: number;
  category: string;
  tags: string[];
}

export default function VideoCard({ id, title, thumbnail, duration, views, rating, category, tags }: VideoCardProps) {
  const { t } = useTranslation();

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <Link
      to={`/article/${id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <span className="absolute bottom-2 right-2 bg-black/75 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
          {duration}
        </span>
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/75 text-accent-400 text-xs px-2 py-0.5 rounded-md">
          <i className="ri-star-fill text-[10px]"></i>
          <span>{rating}</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50">
            <i className="ri-play-fill text-xl ml-0.5"></i>
          </span>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {title}
        </h3>
        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(views)}{t('views')}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500"></i>
            {rating}
          </span>
        </div>
        {tags.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}